import { useState, useEffect } from 'react';
import { Users, RefreshCw, AlertTriangle } from 'lucide-react';
import { ReportChart } from './teamReportChart.jsx';
import { getMembersReport } from '../api.js';

// "Miembros activos" — insured LIVES, not households.
//
// A client is a household; a member is a person covered by a policy. A family
// of four is 1 client and 4 members, and those four may sit on the health
// policy while only two sit on the dental one. That distinction is the whole
// point of this view: the clients report already answers "how many households",
// and it cannot answer "how many lives in dental".
//
// Two totals are shown side by side on purpose (see reports.routes.ts#/members):
//   • Miembros activos  — sum across policies. Health + dental counts that
//                         person twice. This is what breaks down BY TYPE.
//   • Miembros únicos    — distinct people, counted once.
// The type rows therefore add up to the FIRST number, never the second. Showing
// only one of them makes the other look like a bug, so both stay.
//
// Policies where nobody was ever marked insured are surfaced as "sin definir"
// rather than guessed at — the policy card assumes primary+spouse for display,
// but a KPI that invents lives is worse than one that reports the hole.
export function MembersReportView({ lang }) {
  const es = lang === 'es';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  // Filtro por AÑO de la fecha efectiva (Lissette 2026-10-20). '' = todos.
  const [year, setYear] = useState('');
  const anoActual = new Date().getFullYear();
  const anos = [anoActual + 1, anoActual, anoActual - 1, anoActual - 2, anoActual - 3, anoActual - 4, anoActual - 5];
  // Chart controls, same set as every other report in the app.
  const [dimension, setDimension] = useState('type'); // type | agent
  const [chartType, setChartType] = useState('bar');
  const [chartKey, setChartKey] = useState('members');

  const load = async (nextStatus = status, nextYear = year) => {
    setLoading(true);
    setError('');
    try {
      setData(await getMembersReport({ status: nextStatus, year: nextYear }));
    } catch (err) {
      setError(err.message || (es ? 'No se pudo cargar el reporte' : 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(status, year); /* eslint-disable-next-line */ }, [status, year]);

  const TYPE_LABELS = {
    HEALTH: { es: 'Salud', en: 'Health' }, LIFE: { es: 'Vida', en: 'Life' },
    AUTO: { es: 'Auto', en: 'Auto' }, HOME: { es: 'Hogar', en: 'Home' },
    RENTERS: { es: 'Inquilinos', en: 'Renters' }, FLOOD: { es: 'Inundación', en: 'Flood' },
    DENTAL: { es: 'Dental', en: 'Dental' }, VISION: { es: 'Visión', en: 'Vision' },
    DISABILITY: { es: 'Incapacidad', en: 'Disability' }, LONG_TERM_CARE: { es: 'Cuidado LP', en: 'Long-term Care' },
    MEDICARE_SUPPLEMENT: { es: 'Medicare Supl.', en: 'Medicare Supp.' }, MEDICARE_ADVANTAGE: { es: 'Medicare Adv.', en: 'Medicare Adv.' },
    ANNUITY: { es: 'Anualidad', en: 'Annuity' }, UMBRELLA: { es: 'Paraguas', en: 'Umbrella' },
    COMMERCIAL: { es: 'Comercial', en: 'Commercial' }, WORKERS_COMP: { es: 'Comp. Trab.', en: "Workers' Comp" },
    PET: { es: 'Mascotas', en: 'Pet' }, TRAVEL: { es: 'Viaje', en: 'Travel' },
  };
  const typeLabel = (t) => (TYPE_LABELS[t] ? TYPE_LABELS[t][es ? 'es' : 'en'] : t);
  const n = (v) => Number(v || 0).toLocaleString('en-US');

  const card = { background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.85rem' };
  const kpiLabel = { fontSize: '.62rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.4px' };
  const kpiVal = { fontSize: '1.35rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)' };
  const sectionTitle = { fontSize: '.78rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: '.5rem' };
  const th2 = { padding: '.5rem .6rem', fontWeight: 700, fontSize: '.66rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.3px', whiteSpace: 'nowrap' };
  const td2 = { padding: '.45rem .6rem', fontSize: '.76rem', color: 'var(--c-white,#eef6ff)', whiteSpace: 'nowrap' };

  // Feed the SHARED chart component instead of a hand-rolled one, so this view
  // gets the same bar/pie/table controls, direct labels and "Otros" folding as
  // every other report — and so a fix in one place lands everywhere.
  const chartRows = dimension === 'agent'
    ? (data?.byAgent || []).map((r) => ({ ...r, name: r.name }))
    : (data?.byType || []).map((r) => ({ ...r, name: typeLabel(r.type) }));

  const chartMeta = {
    color: '#00d4ff',
    columns: [
      // All three are plain counts that genuinely divide a whole, so a pie is
      // honest for any of them. `avgMembersPerPolicy` is deliberately absent:
      // it is an average, and a pie of averages claims a total that isn't real.
      { key: 'members', label: es ? 'Miembros' : 'Members', raw: (r) => Number(r.members || 0), unit: 'num', additive: true },
      { key: 'uniqueMembers', label: es ? 'Miembros únicos' : 'Unique members', raw: (r) => Number(r.uniqueMembers || 0), unit: 'num', additive: true },
      { key: 'policies', label: es ? 'Pólizas' : 'Policies', raw: (r) => Number(r.policies || 0), unit: 'num', additive: true },
    ],
  };
  const chartInp = { padding: '.35rem .6rem', borderRadius: 8, background: 'var(--c-surface2,#112538)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', fontSize: '.78rem', outline: 'none', fontFamily: 'inherit' };
  const t = (a, b) => (es ? a : b);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '.6rem' }}>
        <Users size={18} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
        <div style={{ marginRight: 'auto' }}>
          <div style={{ fontSize: '.95rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)' }}>
            {es ? 'Miembros Activos' : 'Active Members'}
          </div>
          <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>
            {es
              ? 'Vidas aseguradas por tipo de póliza — un cliente es un hogar, un miembro es una persona.'
              : 'Insured lives by policy type — a client is a household, a member is a person.'}
          </div>
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ background: 'var(--c-surface,#0d1c2e)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.35rem .6rem', fontSize: '.72rem' }}
        >
          <option value="ACTIVE">{es ? 'Pólizas activas' : 'Active policies'}</option>
          <option value="PENDING">{es ? 'Pendientes' : 'Pending'}</option>
          <option value="EXPIRED">{es ? 'Expiradas' : 'Expired'}</option>
          <option value="CANCELLED">{es ? 'Canceladas' : 'Cancelled'}</option>
        </select>
        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          title={es ? 'Año de la fecha efectiva de la póliza' : 'Policy effective-date year'}
          style={{ background: 'var(--c-surface,#0d1c2e)', color: 'var(--c-white,#eef6ff)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.35rem .6rem', fontSize: '.72rem' }}
        >
          <option value="">{es ? 'Todos los años' : 'All years'}</option>
          {anos.map((a) => <option key={a} value={String(a)}>{a}</option>)}
        </select>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: '.35rem', background: 'var(--c-surface,#0d1c2e)', color: 'var(--c-text2,#7aa3c0)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.35rem .6rem', fontSize: '.72rem', cursor: loading ? 'default' : 'pointer' }}
        >
          <RefreshCw size={13} /> {es ? 'Actualizar' : 'Refresh'}
        </button>
      </div>

      {loading && <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{es ? 'Cargando…' : 'Loading…'}</div>}
      {error && !loading && (
        <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.35)', borderRadius: 10, padding: '.75rem 1rem', color: '#fca5a5', fontSize: '.8rem' }}>{error}</div>
      )}

      {!loading && !error && data && (
        <>
          {/* ── KPI tiles ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '.6rem' }}>
            <div style={card}>
              <div style={kpiLabel}>{es ? 'Miembros activos' : 'Active members'}</div>
              <div style={{ ...kpiVal, color: 'var(--c-cyan,#00d4ff)' }}>{n(data.totals.memberPolicies)}</div>
              <div style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)' }}>
                {es ? 'suma de todas las líneas' : 'summed across all lines'}
              </div>
            </div>
            <div style={card}>
              <div style={kpiLabel}>{es ? 'Miembros únicos' : 'Unique members'}</div>
              <div style={kpiVal}>{n(data.totals.uniqueMembers)}</div>
              <div style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)' }}>
                {es ? 'personas distintas' : 'distinct people'}
              </div>
            </div>
            <div style={card}>
              <div style={kpiLabel}>{es ? 'Pólizas' : 'Policies'}</div>
              <div style={kpiVal}>{n(data.totals.policies)}</div>
              <div style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)' }}>
                {data.totals.definedPolicies > 0
                  ? `${(data.totals.memberPolicies / data.totals.definedPolicies).toFixed(2)} ${es ? 'miembros por póliza' : 'members per policy'}`
                  : (es ? 'sin datos' : 'no data')}
              </div>
            </div>
            <div style={card}>
              <div style={kpiLabel}>{es ? 'Sin definir' : 'Not set'}</div>
              <div style={{ ...kpiVal, color: data.totals.undefinedPolicies ? 'var(--c-amber,#ffb700)' : 'var(--c-white,#eef6ff)' }}>
                {n(data.totals.undefinedPolicies)}
              </div>
              <div style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)' }}>
                {es ? 'pólizas sin asegurados marcados' : 'policies with nobody marked'}
              </div>
            </div>
          </div>

          {/* Why the two totals differ — without this the numbers read as a bug. */}
          <div style={{ fontSize: '.68rem', color: 'var(--c-text2,#7aa3c0)', background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.6rem .8rem', lineHeight: 1.5 }}>
            {es
              ? <>Una persona con salud <b>y</b> dental cuenta en las dos líneas, por eso <b>miembros activos</b> ({n(data.totals.memberPolicies)}) es mayor que <b>miembros únicos</b> ({n(data.totals.uniqueMembers)}). Las filas de abajo suman al primero, no al segundo.</>
              : <>Someone on health <b>and</b> dental counts in both lines, which is why <b>active members</b> ({n(data.totals.memberPolicies)}) exceeds <b>unique members</b> ({n(data.totals.uniqueMembers)}). The rows below add up to the first, not the second.</>}
          </div>

          {/* ── Pólizas sin selección ── */}
          {data.totals.undefinedPolicies > 0 && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '.5rem', background: 'rgba(255,183,0,.08)', border: '1px solid rgba(255,183,0,.32)', borderRadius: 10, padding: '.6rem .8rem' }}>
              <AlertTriangle size={14} style={{ color: 'var(--c-amber,#ffb700)', flex: 'none', marginTop: 2 }} />
              <div style={{ fontSize: '.7rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5 }}>
                {es
                  ? <><b style={{ color: 'var(--c-amber,#ffb700)' }}>{n(data.totals.undefinedPolicies)} pólizas</b> no tienen marcado quién está asegurado, así que aportan <b>0 miembros</b> a los números de arriba. Ábrelas y marca las personas en la sección «Asegurados en esta póliza» para que cuenten.</>
                  : <><b style={{ color: 'var(--c-amber,#ffb700)' }}>{n(data.totals.undefinedPolicies)} policies</b> have nobody marked as insured, so they contribute <b>0 members</b> above. Open them and tick the people under "Insured on policy" to make them count.</>}
              </div>
            </div>
          )}

          {/* ── Chart ── */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '.5rem', marginBottom: '.6rem' }}>
              <div style={{ ...sectionTitle, marginBottom: 0, marginRight: 'auto' }}>
                {es ? 'Miembros activos' : 'Active members'}
              </div>
              <span style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                {es ? 'Agrupar por' : 'Group by'}
              </span>
              <select value={dimension} onChange={(e) => setDimension(e.target.value)} style={{ ...chartInp, cursor: 'pointer' }}>
                <option value="type">{es ? 'Tipo de póliza' : 'Policy type'}</option>
                <option value="agent">{es ? 'Agente' : 'Agent'}</option>
              </select>
            </div>
            {chartRows.length === 0 ? (
              <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{es ? 'Sin pólizas en este estado' : 'No policies in this status'}</div>
            ) : (
              <ReportChart
                rows={chartRows}
                meta={chartMeta}
                chartType={chartType}
                chartKey={chartKey}
                onChartType={setChartType}
                onChartKey={setChartKey}
                loading={false}
                t={t}
                inp={chartInp}
              />
            )}
          </div>

          {/* ── Table by type ── */}
          <div style={card}>
            <div style={sectionTitle}>{es ? 'Detalle por tipo' : 'Breakdown by type'}</div>
            {(data.byType || []).length === 0 ? (
              <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{es ? 'Sin datos' : 'No data'}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                  <thead>
                    <tr style={{ background: 'var(--c-surface,#0d1c2e)', textAlign: 'left' }}>
                      <th style={th2}>{es ? 'Tipo' : 'Type'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Miembros' : 'Members'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Únicos' : 'Unique'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Pólizas' : 'Policies'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Prom. x póliza' : 'Avg / policy'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Sin definir' : 'Not set'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byType.map((r, i) => (
                      <tr key={r.type} style={{ borderTop: '1px solid var(--c-border,#1a3550)', background: i % 2 ? 'var(--c-surface,#0d1c2e)' : 'transparent' }}>
                        <td style={{ ...td2, fontWeight: 600 }}>{typeLabel(r.type)}</td>
                        <td style={{ ...td2, textAlign: 'right', fontWeight: 700, color: 'var(--c-cyan,#00d4ff)' }}>{n(r.members)}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{n(r.uniqueMembers)}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{n(r.policies)}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{r.avgMembersPerPolicy.toFixed(2)}</td>
                        <td style={{ ...td2, textAlign: 'right', color: r.undefinedPolicies ? 'var(--c-amber,#ffb700)' : 'var(--c-text3,#45688a)' }}>{n(r.undefinedPolicies)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Table by agent ── */}
          {(data.byAgent || []).length > 1 && (
            <div style={card}>
              <div style={sectionTitle}>{es ? 'Miembros por agente' : 'Members by agent'}</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                  <thead>
                    <tr style={{ background: 'var(--c-surface,#0d1c2e)', textAlign: 'left' }}>
                      <th style={th2}>{es ? 'Agente' : 'Agent'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Miembros' : 'Members'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Únicos' : 'Unique'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Pólizas' : 'Policies'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Prom. x póliza' : 'Avg / policy'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Sin definir' : 'Not set'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byAgent.map((r, i) => (
                      <tr key={r.userId} style={{ borderTop: '1px solid var(--c-border,#1a3550)', background: i % 2 ? 'var(--c-surface,#0d1c2e)' : 'transparent' }}>
                        <td style={{ ...td2, fontWeight: 600 }}>{r.name}</td>
                        <td style={{ ...td2, textAlign: 'right', fontWeight: 700, color: 'var(--c-cyan,#00d4ff)' }}>{n(r.members)}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{n(r.uniqueMembers)}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{n(r.policies)}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{r.avgMembersPerPolicy.toFixed(2)}</td>
                        <td style={{ ...td2, textAlign: 'right', color: r.undefinedPolicies ? 'var(--c-amber,#ffb700)' : 'var(--c-text3,#45688a)' }}>{n(r.undefinedPolicies)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MembersReportView;
